import React, { useEffect } from "react";
import { Input, InputNumber } from "antd";
import { MinusOutlined, PlusOutlined  } from "@ant-design/icons";
import PreviewQuestionLabel from "../../../common/PreviewQuestionLabel/PreviewQuestionLabel";
import { useSurveyInputStore } from "../../../../surveyStore";
import PreviewQuestionConstraints from "../../../common/PreviewQuestionConstraints/PreviewQuestionConstraints";
import useEvaluateConstraint from "../../question-preview/hooks/useEvaluateConstraint";
import useEvaluateAppearance from "../../question-preview/hooks/useEvaluateAppearance";

import "./Text.scss";

const { TextArea } = Input;

export const Text = React.memo(
   ({ props, hasErrors, surveyId, userId, submissionId, label, hint, defaultValue }) => {
      const { id, questionType, isRequired, answers } = props;
      const inputState = useSurveyInputStore((state) => state.inputs?.[id]);
      const setInputs = useSurveyInputStore((state) => state.setInputs);

      const hasSubmissionId = Boolean(submissionId);

      const submissionAnswer = hasSubmissionId
         ? answers?.find((answer) =>
              props.repeatInstanceIndex
                 ? answer?.answerDetail.repeatGroup === props.repeatInstanceIndex
                 : answer
           )?.answerDetail?.text
         : null;

      const value = hasSubmissionId ? submissionAnswer : inputState?.answerDetail?.text ?? null;
      const passConstraint = useEvaluateConstraint(
         !hasSubmissionId ? props.constraint : null,
         value
      );

      const appearance = useEvaluateAppearance({
         appearance: props.appearance
      });

      const showErrors = hasErrors && isRequired && !inputState?.answerDetail?.text;

      const onChange = (e) => {
         if (hasSubmissionId) return;

         if(appearance.includes("numbers")){
            if (e !== 0 && !e) {
               setInputs({
                  target: {
                     name: id,
                     value: ""
                  }
               });
            } else {
               setInputs({
                  target: {
                     name: id,
                     value: {
                        surveyId: surveyId,
                        userId: userId,
                        questionId: id,
                        questionType: questionType,
                        name: props.name,
                        skipLogicAnswer: e,
                        answerDetail: {
                           text: e
                        }
                     }
                  }
               });
            }
         } else {
            if (e.target.value.length === 0) {
               setInputs({
                  target: {
                     name: id,
                     value: ""
                  }
               });
            } else {
               setInputs({
                  target: {
                     name: id,
                     value: {
                        surveyId: surveyId,
                        userId: userId,
                        questionId: id,
                        questionType: questionType,
                        name: props.name,
                        skipLogicAnswer: e.target.value,
                        answerDetail: {
                           text: e.target.value
                        }
                     }
                  }
               });
            }
         }
      };

      useEffect(() => {
         if (props.default || defaultValue) {
            onChange({ target: { value: defaultValue ?? props.default } });
         }
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [props.default, defaultValue]);

      const onKeyDown = (event) => {
         if (event.key === "." || event.key === ",") {
            event.preventDefault();
         }
      };

      return (
         <div className="textarea-submission-type-wrapper">
            <PreviewQuestionLabel isRequired={isRequired} hint={hint} label={label} />

            {
               appearance.includes("numbers") ?
               <InputNumber
                  size="large"
                  type="number"
                  name={id}
                  min={0}
                  step={1}
                  onKeyDown={onKeyDown}
                  readOnly={hasSubmissionId}
                  status={showErrors || (passConstraint !== null && !passConstraint) ? "error" : ""}
                  placeholder={"Type your answer here"}
                  value={value}
                  onChange={onChange}
                  controls={{
                     downIcon: <MinusOutlined style={{ fontSize: 16 }} />,
                     upIcon: <PlusOutlined style={{ fontSize: 16 }} />
                  }}
                  prefixCls="number-submission-type-wrapper--number"
               />
               :
               <TextArea
                  rows={4}
                  required={isRequired}
                  status={showErrors || (passConstraint !== null && !passConstraint) ? "error" : ""}
                  placeholder="Type your answer here"
                  readOnly={hasSubmissionId}
                  name={id}
                  value={value}
                  onChange={onChange}
                  className="textarea-submission-type-wrapper--textarea"
                  maxLength={30000}
               />
            }

            <span className="textarea-submission-type-wrapper--error">
               {showErrors ? "This field is required" : " "}
               {passConstraint !== null && !passConstraint ? (
                  <PreviewQuestionConstraints constraintMessages={props.constraintMessages} />
               ) : null}
            </span>
         </div>
      );
   }
);
